import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Lock, Plus, Save, X } from "lucide-react";
import { toast } from "sonner";

const LISTS = [
  ["currencies", "Currency", "e.g. TZS"],
  ["incoterms", "Incoterms", "e.g. EXW Dar Es Salaam, Tanzania"],
  ["payment_terms", "Payment Terms", "e.g. 100% on PO Placement"],
  ["validity", "Quote Validity", "e.g. 30 Days"],
];

const OptionList = ({ k, label, placeholder, items, setItems, readOnly }) => {
  const [draft, setDraft] = useState("");
  const add = () => { const v = draft.trim(); if (v && !items.some((x) => x.toLowerCase() === v.toLowerCase())) setItems([...items, v]); setDraft(""); };
  return (
    <div className="rounded-lg border border-border bg-white p-4" data-testid={`qo-list-${k}`}>
      <div className="mb-2 text-sm font-semibold text-slate-800">{label}</div>
      <div className="flex flex-wrap gap-1.5">
        {items.map((o, i) => (
          <span key={o} className="inline-flex items-center gap-1 rounded-full border border-[#1B4383]/30 bg-[#E6ECF5] px-2.5 py-1 text-xs text-[#1B4383]" data-testid={`qo-${k}-${i}`}>
            {o}
            {!readOnly && items.length > 1 && <button type="button" onClick={() => setItems(items.filter((x) => x !== o))} data-testid={`qo-${k}-remove-${i}`}><X className="h-3 w-3" /></button>}
          </span>
        ))}
      </div>
      {!readOnly && (
        <div className="mt-2 flex gap-2">
          <Input className="h-8" placeholder={placeholder} value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} data-testid={`qo-${k}-input`} />
          <Button type="button" variant="outline" size="sm" onClick={add} data-testid={`qo-${k}-add`}><Plus className="mr-1 h-3.5 w-3.5" />Add</Button>
        </div>
      )}
    </div>
  );
};

export const QuoteOptionsCard = ({ isOwner }) => {
  const [opts, setOpts] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    let live = true;
    api.get("/settings/quote-options").then((r) => { if (live) setOpts((cur) => cur || r.data); }).catch(() => {});
    return () => { live = false; };
  }, []);
  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put("/settings/quote-options", opts); setOpts(data); toast.success("Quote options saved"); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  if (!opts) return <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Loading…</div>;
  return (
    <div className="space-y-4" data-testid="quote-options-card">
      <p className="text-sm text-muted-foreground">These are the only choices shown in the Currency, Incoterms, Payment Terms and Quote Validity dropdowns on every Somcore Quote.</p>
      {!isOwner && <p className="inline-flex items-center gap-1.5 text-xs text-muted-foreground"><Lock className="h-3.5 w-3.5" />Only Ronick can change these options.</p>}
      <div className="grid gap-4 sm:grid-cols-2">
        {LISTS.map(([k, label, ph]) => (
          <OptionList key={k} k={k} label={label} placeholder={ph} items={opts[k] || []} setItems={(v) => setOpts({ ...opts, [k]: v })} readOnly={!isOwner} />
        ))}
      </div>
      <p className="text-[11px] text-muted-foreground">Exchange-rate conversion on the quote is calculated between TZS and USD.</p>
      {isOwner && <div className="flex justify-end"><Button onClick={save} disabled={saving} className="bg-[#1B4383] hover:bg-[#16386e]" data-testid="qo-save">{saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}Save Quote Options</Button></div>}
    </div>
  );
};

export default QuoteOptionsCard;
